import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, Package, Truck, Clock, ExternalLink, PackageSearch, RotateCcw, PackageX, Camera, X } from "lucide-react";

const STATUS_MAP = {
  processing: { label: "Payment received — building your order", icon: Clock },
  submitted: { label: "In production", icon: Package },
  in_production: { label: "In production", icon: Package },
  shipped: { label: "Shipped", icon: Truck },
  delivered: { label: "Delivered", icon: CheckCircle2 },
  printify_failed: { label: "Payment received", icon: CheckCircle2 },
};

export default function TrackOrder() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reordering, setReordering] = useState(false);
  const [mode, setMode] = useState("id"); // 'id' | 'email'
  const [email, setEmail] = useState("");
  const [orderList, setOrderList] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returnType, setReturnType] = useState("defective");
  const [returnEmail, setReturnEmail] = useState("");
  const [returnFiles, setReturnFiles] = useState([]);
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  const runLookup = async (id) => {
    if (!id) return;
    setLoading(true);
    setError("");
    setOrder(null);
    setOrderList(null);
    try {
      const r = await api.get(`/orders/${id}`);
      setOrder(r.data);
    } catch {
      setError("We couldn't find an order with that ID. Check the confirmation email you received.");
    } finally {
      setLoading(false);
    }
  };

  const emailLookup = async (e) => {
    e?.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) return setError("Enter a valid email.");
    setLoading(true);
    setError("");
    setOrder(null);
    setOrderList(null);
    try {
      const r = await api.get(`/orders?email=${encodeURIComponent(em)}`);
      if (!r.data.length) setError("No orders found for that email yet.");
      else setOrderList(r.data);
    } catch {
      setError("Lookup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const preId = new URLSearchParams(search).get("id");
    if (preId) {
      setOrderId(preId);
      runLookup(preId.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const lookup = (e) => {
    e?.preventDefault();
    runLookup(orderId.trim());
  };

  const reorder = async () => {
    if (!order?.line_items?.length) return;
    setReordering(true);
    let added = 0;
    let missing = 0;
    try {
      for (const li of order.line_items) {
        try {
          const p = (await api.get(`/products/${li.product_id}`)).data;
          const v = p.variants?.find((x) => x.id === li.variant_id && x.is_enabled);
          if (!v) { missing += 1; continue; }
          for (let q = 0; q < (li.quantity || 1); q++) {
            addItem(p, v, { name: li.custom_name, number: li.custom_number });
          }
          added += 1;
        } catch {
          missing += 1;
        }
      }
      if (added > 0) {
        toast.success(missing ? `Added ${added} item(s) — ${missing} no longer available` : "Added to your cart");
        setOpen(true);
        navigate("/checkout");
      } else {
        toast.error("These items are no longer available to reorder.");
      }
    } finally {
      setReordering(false);
    }
  };

  const submitReturn = async () => {
    const buyerEmail = (returnEmail || email || "").trim().toLowerCase();
    if (!buyerEmail || !buyerEmail.includes("@")) {
      toast.error("Enter the email you used on this order.");
      return;
    }
    setReturnSubmitting(true);
    try {
      const photo_paths = [];
      for (const f of returnFiles.slice(0, 3)) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await api.post("/returns/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_paths.push(up.data.path);
      }
      const res = await api.post(`/orders/${order.id}/request-return`, { email: buyerEmail, reason: returnReason.trim(), reason_type: returnType, photo_paths });
      const newStatus = res?.data?.status === "replaced" ? "replaced" : "requested";
      toast.success(newStatus === "replaced"
        ? "Claim approved — a free replacement is on the way!"
        : "Claim received — we'll email you shortly.");
      setReturnOpen(false);
      setReturnReason("");
      setOrder({ ...order, return_status: newStatus, return_eligible: false });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't submit the return. Please try again.");
    } finally {
      setReturnSubmitting(false);
    }
  };

  const RETURN_LABELS = {
    requested: "Claim submitted — under review",
    approved: "Approved & refunded",
    replaced: "Free replacement on the way",
    denied: "Claim declined",
  };

  const statusKey = order?.status || "processing";
  const statusInfo = STATUS_MAP[statusKey] || STATUS_MAP.processing;
  const StatusIcon = statusInfo.icon;
  const tracking = order?.tracking;
  const isFulfilling = statusKey === "processing";

  return (
    <div data-testid="track-page" className="max-w-2xl mx-auto px-6 py-24 bg-background min-h-screen">
      <div className="text-center">
        <PackageSearch className="w-14 h-14 text-primary mx-auto" />
        <h1 className="font-anton text-4xl sm:text-5xl uppercase tracking-tight mt-6 text-white">Track your order</h1>
        <p className="text-white/60 mt-3">Look up by Order ID, or find all your orders by email.</p>
      </div>

      <div className="mt-8 flex gap-2">
        <button
          data-testid="track-mode-id"
          onClick={() => { setMode("id"); setError(""); }}
          className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-[0.2em] border transition-colors ${mode === "id" ? "bg-primary text-black border-primary" : "bg-transparent text-white/60 border-white/20 hover:border-white/40"}`}
        >
          By Order ID
        </button>
        <button
          data-testid="track-mode-email"
          onClick={() => { setMode("email"); setError(""); }}
          className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-[0.2em] border transition-colors ${mode === "email" ? "bg-primary text-black border-primary" : "bg-transparent text-white/60 border-white/20 hover:border-white/40"}`}
        >
          By Email
        </button>
      </div>

      {mode === "id" ? (
        <form onSubmit={lookup} className="mt-4 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="track-input"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Order ID (e.g. 3f9a1c2b-…)"
            className="flex-1 bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/30"
          />
          <button
            type="submit"
            data-testid="track-submit"
            disabled={loading || !orderId.trim()}
            className="flex items-center justify-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-40 px-8 py-3.5 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Track
          </button>
        </form>
      ) : (
        <form onSubmit={emailLookup} className="mt-4 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="track-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="flex-1 bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/30"
          />
          <button
            type="submit"
            data-testid="track-email-submit"
            disabled={loading || !email.trim()}
            className="flex items-center justify-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-40 px-8 py-3.5 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find
          </button>
        </form>
      )}

      {error && (
        <p data-testid="track-error" className="mt-6 text-sm text-red-400 border border-red-400/30 bg-red-400/5 p-4">
          {error}
        </p>
      )}

      {orderList && (
        <div className="mt-8 space-y-3" data-testid="track-order-list">
          {orderList.map((o) => (
            <button
              key={o.id}
              data-testid="track-list-item"
              onClick={() => { setOrder(o); setOrderList(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="w-full flex items-center justify-between gap-4 border border-white/10 hover:border-primary bg-card p-4 text-left transition-colors"
            >
              <div>
                <p className="font-bold text-white text-sm">#{o.id.slice(0, 8)}</p>
                <p className="text-[11px] text-white/40 uppercase tracking-widest">
                  {(STATUS_MAP[o.status] || STATUS_MAP.processing).label} · {o.line_items?.length || 0} item(s)
                </p>
              </div>
              <span className="font-bold text-white whitespace-nowrap">{formatPrice(o.total_price)}</span>
            </button>
          ))}
        </div>
      )}

      {order && (
        <div className="mt-10 border border-white/10 bg-card text-left p-6" data-testid="track-result">
          <div className="flex justify-between text-sm mb-3">
            <span className="text-white/50 uppercase tracking-widest">Order ID</span>
            <span className="font-bold text-white">{order.id.slice(0, 8)}</span>
          </div>
          <div className="flex justify-between text-sm mb-3">
            <span className="text-white/50 uppercase tracking-widest">Payment</span>
            <span className="font-bold uppercase text-primary">Paid</span>
          </div>
          <div className="flex justify-between items-center text-sm mb-4">
            <span className="text-white/50 uppercase tracking-widest">Status</span>
            <span className="flex items-center gap-2 font-bold text-white" data-testid="track-status">
              {isFulfilling ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <StatusIcon className="w-4 h-4 text-primary" />}
              {statusInfo.label}
            </span>
          </div>

          {tracking?.number && (
            <div className="border-t border-white/10 pt-4 mb-4" data-testid="track-tracking-block">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-white/50 uppercase tracking-widest">Carrier</span>
                <span className="font-bold text-white">{tracking.carrier || "—"}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/50 uppercase tracking-widest">Tracking</span>
                {tracking.url ? (
                  <a
                    href={tracking.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="track-tracking-link"
                    className="flex items-center gap-1.5 font-bold text-primary hover:text-white transition-colors"
                  >
                    {tracking.number}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span className="font-bold text-white">{tracking.number}</span>
                )}
              </div>
            </div>
          )}

          {Array.isArray(order.line_items) && order.line_items.length > 0 && (
            <div className="border-t border-white/10 pt-4 space-y-3" data-testid="track-items">
              {order.line_items.map((li, i) => (
                <div key={`${li.product_id}-${li.variant_id}-${i}`} className="flex gap-3 items-center">
                  {li.image && (
                    <img src={li.image} alt={li.title} className="w-12 h-14 object-contain bg-neutral-900 rounded-sm shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white text-sm truncate">{li.title}</p>
                    <p className="text-[11px] text-white/40 uppercase tracking-widest">{li.size} · Qty {li.quantity}</p>
                    {(li.custom_name || li.custom_number) && (
                      <p className="text-[11px] text-primary uppercase tracking-widest mt-0.5">
                        {li.custom_name}{li.custom_name && li.custom_number ? " · " : ""}
                        {li.custom_number ? `#${li.custom_number}` : ""}
                      </p>
                    )}
                  </div>
                  <span className="text-sm font-bold text-white whitespace-nowrap">{formatPrice(li.price * li.quantity)}</span>
                </div>
              ))}
            </div>
          )}

          {order.shipping_method_label && (
            <div className="flex justify-between items-center text-sm border-t border-white/10 mt-4 pt-4" data-testid="track-delivery">
              <span className="text-white/50 uppercase tracking-widest">Delivery</span>
              <span className="font-bold text-white text-right">
                {order.shipping_method_label}
                {order.shipping_estimate && <span className="block text-[11px] text-white/40 font-normal normal-case tracking-normal">Arrives in {order.shipping_estimate}</span>}
              </span>
            </div>
          )}

          <div className="flex justify-between text-sm border-t border-white/10 mt-4 pt-4">
            <span className="text-white/50 uppercase tracking-widest">Total</span>
            <span className="font-bold text-white">{formatPrice(order.total_price)}</span>
          </div>

          <button
            data-testid="reorder-button"
            onClick={reorder}
            disabled={reordering}
            className="mt-5 w-full flex items-center justify-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-40 px-6 py-3.5 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95"
          >
            {reordering ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Reorder these items
          </button>

          {/* Returns */}
          {order.return_status ? (
            <div data-testid="return-status" className="mt-4 border border-white/15 bg-white/5 p-4">
              <div className="flex items-center gap-3">
                <PackageX className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm font-bold uppercase tracking-widest text-white">
                  {RETURN_LABELS[order.return_status] || "Return in progress"}
                </span>
              </div>
              {order.replacement && (
                <div data-testid="replacement-info" className="mt-3 pt-3 border-t border-white/10 text-sm">
                  <p className="text-white/60">
                    Replacement order <span className="text-white font-bold">#{order.replacement.id.slice(0, 8).toUpperCase()}</span>
                    {order.replacement.status ? <span className="text-white/40"> · {order.replacement.status.replace(/_/g, " ")}</span> : null}
                  </p>
                  {order.replacement.tracking?.url ? (
                    <a
                      data-testid="replacement-tracking-link"
                      href={order.replacement.tracking.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-1.5 text-primary hover:text-white font-bold uppercase tracking-widest text-xs transition-colors"
                    >
                      <Truck className="w-4 h-4" /> Track replacement <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <p className="mt-1 text-xs text-white/40">Tracking will appear here once your replacement ships.</p>
                  )}
                </div>
              )}
            </div>
          ) : order.return_eligible ? (
            !returnOpen ? (
              <button
                data-testid="request-return-button"
                onClick={() => { setReturnOpen(true); setReturnEmail(email || ""); }}
                className="mt-3 w-full flex items-center justify-center gap-2 border border-white/25 text-white hover:border-primary hover:text-primary px-6 py-3 font-bold uppercase tracking-[0.2em] text-xs transition-colors"
              >
                <PackageX className="w-4 h-4" /> Report a problem
              </button>
            ) : (
              <div data-testid="return-form" className="mt-3 border border-white/15 p-4 space-y-3">
                <p className="text-sm font-bold uppercase tracking-widest text-white">Report a problem</p>
                <p className="text-xs text-white/50">
                  Each jersey is custom made-to-order, so we can only accept returns for items that arrive
                  <b className="text-white/70"> damaged or with a manufacturing error</b> (not for sizing or change-of-mind).
                </p>
                <div className="space-y-2">
                  {[
                    ["defective", "Item arrived damaged or defective", true],
                    ["error", "Manufacturing error (wrong print / color)", true],
                    ["size", "Wrong size", false],
                    ["other", "Changed my mind / other", false],
                  ].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`return-type-${val}`}
                      onClick={() => setReturnType(val)}
                      className={`w-full flex items-center gap-3 p-2.5 border text-left text-sm transition-colors ${
                        returnType === val ? "border-primary bg-primary/5 text-white" : "border-white/15 text-white/70 hover:border-white/30"
                      }`}
                    >
                      <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 ${returnType === val ? "border-primary bg-primary" : "border-white/40"}`} />
                      {label}
                    </button>
                  ))}
                </div>
                {["size", "other"].includes(returnType) ? (
                  <p data-testid="return-policy-note" className="text-xs text-amber-300/90 border border-amber-500/30 bg-amber-500/5 p-3">
                    Because your jersey is printed just for you, we can't accept returns for sizing or change-of-mind.
                    Please check the size chart on the product page. If your item is damaged or defective, pick one of the
                    first two options and we'll make it right.
                  </p>
                ) : (
                  <>
                    <input
                      data-testid="return-email-input"
                      type="email"
                      value={returnEmail}
                      onChange={(e) => setReturnEmail(e.target.value)}
                      placeholder="Email used on this order"
                      className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
                    />
                    <textarea
                      data-testid="return-reason-input"
                      value={returnReason}
                      onChange={(e) => setReturnReason(e.target.value)}
                      placeholder="Describe the issue — a photo helps us process it faster"
                      rows={3}
                      className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm resize-none"
                    />
                    <label className="block">
                      <span className="text-xs uppercase tracking-widest text-white/50 flex items-center gap-2 mb-1.5">
                        <Camera className="w-4 h-4" /> Attach photos of the damage — up to 3 angles {returnFiles.length ? `(${returnFiles.length}/3)` : "(at least one required)"}
                      </span>
                      <input
                        data-testid="return-photo-input"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/heic"
                        multiple
                        disabled={returnFiles.length >= 3}
                        onChange={(e) => {
                          const picked = Array.from(e.target.files || []);
                          setReturnFiles((prev) => [...prev, ...picked].slice(0, 3));
                          e.target.value = "";
                        }}
                        className="block w-full text-xs text-white/60 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-primary file:text-black file:font-bold file:uppercase file:tracking-widest file:cursor-pointer hover:file:bg-white disabled:opacity-40"
                      />
                    </label>
                    {returnFiles.length > 0 && (
                      <div data-testid="return-photo-list" className="flex flex-wrap gap-2">
                        {returnFiles.map((f, i) => (
                          <div key={i} className="relative group">
                            <img src={URL.createObjectURL(f)} alt={`angle ${i + 1}`} className="w-16 h-16 object-cover border border-white/20" />
                            <button
                              type="button"
                              data-testid={`return-photo-remove-${i}`}
                              onClick={() => setReturnFiles((prev) => prev.filter((_, idx) => idx !== i))}
                              className="absolute -top-2 -right-2 bg-black border border-white/30 text-white/80 hover:text-red-400 hover:border-red-500 rounded-full w-5 h-5 flex items-center justify-center text-xs"
                              aria-label="Remove photo"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <div className="flex gap-3">
                  <button
                    data-testid="return-submit-button"
                    onClick={submitReturn}
                    disabled={returnSubmitting || ["size", "other"].includes(returnType) || returnFiles.length === 0}
                    className="flex-1 flex items-center justify-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed px-4 py-3 font-bold uppercase tracking-widest text-xs transition-colors"
                  >
                    {returnSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit claim
                  </button>
                  <button
                    data-testid="return-cancel-button"
                    onClick={() => setReturnOpen(false)}
                    className="px-4 py-3 border border-white/20 text-white/60 hover:text-white font-bold uppercase tracking-widest text-xs"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )
          ) : null}

          {isFulfilling && (
            <p className="text-xs text-white/40 mt-4">
              Your personalized jersey is heading into production — tracking will appear here once it ships.
            </p>
          )}
        </div>
      )}

      <div className="text-center">
        <Link to="/#shop" className="inline-block mt-10 text-primary underline underline-offset-4 font-bold uppercase tracking-widest text-sm hover:text-white transition-colors">
          Keep shopping
        </Link>
      </div>
    </div>
  );
}
